import type { ReportContentBlock, ReportSection } from "./domain";
import { resolveSectionTenLineageState } from "./section10-lineage";

export type ClientContentState =
  | "Current approved"
  | "Unapproved replacement"
  | "Stale preserved wording"
  | "Legacy approved wording"
  | "Unavailable";

export interface ClientSectionContentState {
  state: ClientContentState;
  source: "current blocks" | "workflow draft" | "v2 draft" | "legacy blocks" | "none";
  blocks: ReportContentBlock[];
  contentAvailable: boolean;
  currentForApproval: boolean;
  persistentPageWarningRequired: boolean;
  sectionStateLabel: ClientContentState;
  reason: string;
}

export const CLIENT_PORTAL_REVIEW_ACTION_LABEL = "Mark this version reviewed";
export const CLIENT_PORTAL_REVIEW_INVALIDATION_MESSAGE =
  "Any later client-visible edit invalidates this review.";

const visibleBlocks = (section: ReportSection) =>
  (section.blocks ?? []).filter(block => block.clientVisible && block.content.trim());

const workflowApprovalRecorded = (section: ReportSection) => {
  const workflows = [
    section.sectionOneWorkflow,
    section.sectionTwoWorkflow,
    section.sectionThreeWorkflow,
    section.sectionFourWorkflow,
    section.sectionFiveWorkflow,
    section.sectionSixWorkflow,
    section.sectionSevenWorkflow,
    section.sectionEightWorkflow,
    section.sectionNineWorkflow,
  ].filter(Boolean) as Array<Record<string, unknown>>;
  return workflows.some(value =>
    value.clientDraftApproved === true ||
    value.simplifiedClientDraftApproved === true ||
    Boolean(value.approvedPromptResults)
  );
};

const result = (
  state: ClientContentState,
  source: ClientSectionContentState["source"],
  blocks: ReportContentBlock[],
  reason: string,
): ClientSectionContentState => ({
  state,
  source,
  blocks,
  contentAvailable: blocks.length > 0 || source === "v2 draft" || source === "workflow draft",
  currentForApproval: state === "Current approved",
  persistentPageWarningRequired: state !== "Current approved",
  sectionStateLabel: state,
  reason,
});

export function resolveClientSectionContentState(
  section: ReportSection,
  nativeReadiness?: { ready: boolean; missing: string[] },
): ClientSectionContentState {
  const blocks = visibleBlocks(section);
  if (section.id === "work-programme" && section.sectionTenWorkflow?.redesignVersion === 2) {
    const workflow = section.sectionTenWorkflow;
    const v2Blocks = blocks.filter(block => block.id.startsWith("section10-client-v2-"));
    const legacyBlocks = blocks.filter(block => !block.id.startsWith("section10-client-v2-"));
    const draft = workflow.v2ClientDraft;
    const lineage=resolveSectionTenLineageState(workflow),draftIsCurrent=lineage.draftRelationship==="Current";
    if (lineage.approvalCurrent && draftIsCurrent && v2Blocks.length && nativeReadiness?.ready) {
      return result("Current approved", "current blocks", v2Blocks, "The approved v2 blocks match the current confirmed assessment.");
    }
    if (draft) {
      if (!draftIsCurrent) {
        return result("Stale preserved wording", "v2 draft", v2Blocks, "The preserved v2 draft does not match the current confirmed assessment.");
      }
      return result("Unapproved replacement", "v2 draft", v2Blocks, "The replacement wording exists but has not been explicitly approved.");
    }
    if (v2Blocks.length) {
      return result("Legacy approved wording", "legacy blocks", v2Blocks, "Stored v2 blocks exist without a current verifiable draft relationship.");
    }
    if (legacyBlocks.length) {
      return result("Legacy approved wording", "legacy blocks", legacyBlocks, "Earlier client-visible Section 10 blocks remain preserved.");
    }
    return result("Unavailable", "none", [], "No client-visible Section 10 content is available.");
  }

  const workflowRecords = [
    section.sectionOneWorkflow,
    section.sectionTwoWorkflow,
    section.sectionThreeWorkflow,
    section.sectionFourWorkflow,
    section.sectionFiveWorkflow,
    section.sectionSixWorkflow,
    section.sectionSevenWorkflow,
    section.sectionEightWorkflow,
    section.sectionNineWorkflow,
  ].filter(Boolean) as Array<Record<string, unknown>>;
  const hasWorkflowDraft = workflowRecords.some(value => Boolean(value.clientDraft || value.simplifiedClientDraft));
  const stale = nativeReadiness?.missing.some(item => /stale|changed|refresh|newer|current/i.test(item)) ?? false;
  if (hasWorkflowDraft && (!workflowApprovalRecorded(section) || stale)) {
    return result(
      stale ? "Stale preserved wording" : "Unapproved replacement",
      "workflow draft",
      blocks,
      stale ? "The proposed client wording is preserved but its source is no longer current." : "Proposed client wording exists but has not been explicitly approved.",
    );
  }
  if (blocks.length && nativeReadiness?.ready) {
    return result("Current approved", "current blocks", blocks, "The section has current client-visible content and passes its native readiness rules.");
  }
  if (blocks.length && workflowApprovalRecorded(section)) {
    return result(
      stale ? "Stale preserved wording" : "Legacy approved wording",
      "legacy blocks",
      blocks,
      stale ? "Previously approved wording is preserved but its source is no longer current." : "Approved wording is preserved but is not proven current by the section's native rules.",
    );
  }
  if (blocks.length) {
    return result("Unapproved replacement", "current blocks", blocks, "Client-visible wording exists without a current native approval.");
  }
  return result("Unavailable", "none", [], "No client-visible content is available.");
}

export function resolveClientReportContentStates(
  sections: ReportSection[],
  readinessById: Record<string, { ready: boolean; missing: string[] }>,
) {
  return Object.fromEntries(
    sections.map(section => [section.id, resolveClientSectionContentState(section, readinessById[section.id])]),
  ) as Record<string, ClientSectionContentState>;
}

export function clientPortalPreviewCanBeReviewed(
  states:Record<string,ClientSectionContentState>,
  locked:boolean,
  persistenceStatus:string,
) {
  return !locked &&
    persistenceStatus === "Saved locally" &&
    Object.values(states).length > 0 &&
    Object.values(states).every(state => state.currentForApproval);
}
